<template>
  <Dialog
    is-persistent
    :title="dialogTitle"
    :dialog="isOpened"
    :confirmation-button-text="confirmationButtonText"
    @confirmationAction="addEstimate"
  >
    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pb-4">
          <v-col>
            <v-combobox
              ref="taskTypeCombobox"
              id="task-type-input"
              v-model="estimateItem.task_type"
              :items="taskTypesByOwner"
              :search-input.sync="searchValue"
              item-text="title"
              item-value="id"
              class="secondary-background"
              label="Task"
              :rules="[(bs) => !!bs || 'Task is required']"
              hide-details
              required
              outlined
              dense
              return-object
              :menu-props="{ closeOnContentClick: false }"
              @change="handleTaskTypeChange"
            >
              <template #no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      To create a new Task, please, input its name and press <kbd>enter</kbd>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-select
              id="role-input"
              v-model="estimateItem.role_id"
              :menu-props="{ offsetY: true }"
              :items="roleItems"
              item-text="name"
              item-value="id"
              class="secondary-background"
              label="Role"
              :rules="[(bs) => !!bs?.length || 'Role is required']"
              hide-details
              required
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-text-field
              id="project-estimate-input"
              v-model="estimateItem.estimate"
              data-cy="project-estimate-input"
              class="secondary-background"
              label="Estimate"
              type="number"
              hide-details
              required
              outlined
              dense
              :rules="[(bs) => (bs >= 0) || 'Estimate is required']"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Dialog>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import { Dialog } from '@/components/shared';
import assignmentStatuses from '@/constants/assignmentStatuses';
import { DateHelper } from '@/helpers/date.helper';
const getInitialState = () => ({
  task_type: '',
  role_id: [],
  estimate: 4,
  project_id: '',
});

export default {
  components: {
    Dialog,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    taskTypeItems: {
      type: Array,
      required: true,
    },
    roleItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialogTitle: 'Add estimation',
      confirmationButtonText: 'Add',
      valid: true,
      estimateItem: getInitialState(),
      searchValue: '',
    };
  },
  computed: {
    ...mapGetters('projects/main', ['project']),
    ...mapGetters('shared/roles', ['projectRoles']),
    ...mapGetters('shared/taskTypes', [
      'taskTypesByOwner',
    ]),

    form() {
      return { ...this.estimateItem };
    },
    assignmentStatusesItems() {
      return Object.values(assignmentStatuses);
    },
  },
  watch: {
    startDate(value) {
      if (!DateHelper.isDayBefore(value, this.endDate)) {
        const newEndDate = DateHelper.toIso(moment(value).endOf('month'));

        this.endDate = newEndDate;
      }

      this.startDate = value;
    },
  },
  async created() {
    document.addEventListener('keydown', this.handleKeypress);
    document.addEventListener('keyup', this.handleKeyup);
    await this.getTaskTypesByOwnerProject({
      projectIds: [this.project.id],
    });
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleKeypress);
    document.removeEventListener('keyup', this.handleKeyup);
  },
  methods: {
    ...mapActions('projects/main', ['addProjectEstimation', 'getProjectEstimations']),
    ...mapActions('shared/taskTypes', ['getAllTaskTypes', 'getTaskTypesByOwnerProject', 'addTaskType', 'createTaskTypeWithoutProject', 'addTaskTypeToProject']),

    async addEstimate(confirmed) {
      if (!confirmed) {
        this.closeDialog();
        return;
      }

      if (!this.validate()) {
        return;
      }

      const taskType = this.estimateItem.task_type?.id || this.estimateItem.task_type;

      try {
        await Promise.all(this.estimateItem.role_id.map((roleId) => this.addProjectEstimation({
          estimation: {
            task_type: taskType,
            role_id: roleId,
            estimate: this.estimateItem.estimate,
            project_id: this.project.id,
          },
        })));

        if (this.estimateItem.task_type?.id) {
          await this.addTaskTypeToProject({
            projectId: this.project.id,
            taskTypeId: this.estimateItem.task_type.id,
          });
        }

        this.$emit('save');
      } finally {
        this.closeDialog();
      }
    },
    closeDialog() {
      this.$emit('close');
      this.reset();
      this.getProjectEstimations({ projectId: this.project.id });
    },
    reset() {
      Object.assign(this.estimateItem, getInitialState());
      this.resetValidation();
    },
    validate() {
      return this.$refs.form.validate();
    },
    handleSearchInput(value) {
      this.assignmentItem.searchField = value;
    },
    handleKeypress(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
      }
    },
    handleKeyup(event) {
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        this.addEstimate();
      }
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    async handleTaskTypeChange(taskType) {
      if (!taskType) {
        return;
      }

      if (typeof taskType === 'string') {
        if (!taskType.trim()) {
          return;
        }

        const response = await this.addTaskType({
          taskTypeTitle: taskType,
          projectId: this.project.id,
        });

        await this.getTaskTypesByOwnerProject({ projectIds: [this.project.id] });

        this.estimateItem.task_type = response;
        this.searchValue = response.title;

        this.$nextTick(() => {
          if (this.$refs.taskTypeCombobox) {
            this.$refs.taskTypeCombobox.isMenuActive = false;
          }
        });
      } else {
        this.estimateItem.task_type = taskType;
        this.searchValue = taskType.title;
      }
    },
  },
};
</script>
